// src/utils/giustificazioniPresenze.js
// ═══════════════════════════════════════════════════════════════════════════
// GIUSTIFICAZIONI DELLE TIMBRATURE MANCANTI (ottobre 2026, W419, regole 66)
// ═══════════════════════════════════════════════════════════════════════════
// Raccolta «giustificazioniPresenze»: { orgId, uid, nome, data "YYYY-MM-DD", mancanza
// ("nessuna" | "uscita"), motivo, stato ("inviata" | "accettata" | "respinta"), createdAt,
// decisoDa, decisoDaNome, decisoIl, notaDecisione }.
//   • la scrive solo l'interessato, sempre «inviata»; non si cancella mai;
//   • la decidono titolare, admin, l'ufficio del personale (Gestione, Amministrazione), chi
//     ha il permesso «Approvazione cartellini» e gli approvatori delegati — mai la propria;
//   • avvisi (campanella e telefono): a chi decide, al titolare e ai PM delle commesse su cui
//     la persona era quel giorno (programmazione o entrata timbrata). Il PM può spegnere
//     questi avvisi dal suo Profilo (avvisiGiustificazioniPresenze = false).
// Il motivo è libero: per la malattia c'è «Registra assenza» (dato sanitario, riservato).
// ═══════════════════════════════════════════════════════════════════════════
import { addDoc, collection, doc, getDoc, getDocs, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import { db } from "../firebase";
import { sezioniUtente } from "./permessiEffettivi";
import {
  dataLunga, TESTO_MANCANZA, livelloObbligo, idProgrammazione, idCartellino, celleDaProgrammazione, dovevaTimbrare, mancanzaGiornata,
} from "./obbligoPresenze";
import { parseData, dataStr, componiPeriodo } from "./periodoLavorativo";

export const MOTIVO_MIN = 5;
export const MOTIVO_MAX = 500;

/** Chi decide le giustificazioni (stessa regola di decidiGiustificazione nelle Regole). */
export function eDecisore(utente, { abilitazioni = {}, approvatori = [] } = {}) {
  if (!utente) return false;
  if (utente.role === "titolare" || utente.role === "admin") return true;
  // ufficio del personale (Gestione aziendale, Amministrazione): come isUfficioPersonale() nelle Regole
  if (utente.ruoloOperativo === "gestione" || utente.ruoloOperativo === "amministrazione") return true;
  if (approvatori.includes(utente.uid)) return true;
  return sezioniUtente(abilitazioni, { ruolo: utente.ruoloOperativo, uid: utente.uid }).has("approvazione-cartellini");
}

/**
 * Destinatari degli avvisi (funzione pura, testabile).
 * @returns [{ uid, comeDecisore }] senza doppioni e senza l'autore
 */
export function destinatariGiustificazione({ autoreUid, utenti = [], abilitazioni = {}, approvatori = [], pmUids = [] }) {
  const out = new Map();
  utenti.filter(u => u.enabled !== false && u.uid && u.uid !== autoreUid).forEach(u => {
    if (eDecisore(u, { abilitazioni, approvatori })) out.set(u.uid, { uid: u.uid, comeDecisore: true });
  });
  pmUids.filter(uid => uid && uid !== autoreUid && !out.has(uid)).forEach(uid => {
    const u = utenti.find(x => x.uid === uid);
    if (!u || u.enabled === false || u.avvisiGiustificazioniPresenze === false) return;
    out.set(uid, { uid, comeDecisore: false });
  });
  return [...out.values()];
}

/** Responsabili delle commesse citate (programmazione del giorno + entrate timbrate). */
async function pmDelleCommesse(ids) {
  const uids = [];
  for (const id of [...new Set(ids.filter(Boolean))]) {
    try {
      const s = await getDoc(doc(db, "projects", id));
      const r = s.exists() ? s.data().responsabileUid : "";
      if (r) uids.push(r);
    } catch { /* commessa non leggibile: nessun PM da avvisare */ }
  }
  return uids;
}

/**
 * Invia la giustificazione e avvisa chi deve saperlo.
 * @param p { orgId, utente: { uid, nome }, giorno: { data, mancanza }, motivo, commesseIds }
 */
export async function inviaGiustificazione({ orgId, utente, giorno, motivo, commesseIds = [] }) {
  const testo = String(motivo || "").trim();
  if (testo.length < MOTIVO_MIN) throw new Error("Scrivi il motivo (almeno qualche parola).");
  const rif = await addDoc(collection(db, "giustificazioniPresenze"), {
    orgId, uid: utente.uid, nome: utente.nome || "", data: giorno.data, mancanza: giorno.mancanza,
    motivo: testo.slice(0, MOTIVO_MAX), stato: "inviata", createdAt: serverTimestamp(),
  });
  // avvisi: un errore qui non annulla la giustificazione già registrata
  try {
    const [uSnap, abSnap, apSnap] = await Promise.all([
      getDocs(query(collection(db, "users"), where("organizationId", "==", orgId), where("enabled", "==", true))),
      getDoc(doc(db, "abilitazioniSezioni", orgId)).catch(() => null),
      getDoc(doc(db, "presenzeApprovatori", orgId)).catch(() => null),
    ]);
    const utenti = uSnap.docs.map(d => ({ uid: d.id, ...d.data() }));
    const abilitazioni = abSnap?.exists() ? abSnap.data() : {};
    const approvatori = apSnap?.exists() ? (apSnap.data().uids || []) : [];
    const pmUids = await pmDelleCommesse(commesseIds);
    const quando = dataLunga(parseData(giorno.data));
    const dest = destinatariGiustificazione({ autoreUid: utente.uid, utenti, abilitazioni, approvatori, pmUids });
    await Promise.all(dest.map(d => addDoc(collection(db, "notifiche"), {
      userId: d.uid, orgId, tipo: "giustificazione_presenze",
      titolo: `🕐 ${utente.nome || "Un collaboratore"}: timbratura mancante ${quando}`,
      testo: `${TESTO_MANCANZA[giorno.mancanza] || "timbratura mancante"} — «${testo.slice(0, 160)}${testo.length > 160 ? "…" : ""}»` +
        (d.comeDecisore ? " · da accettare o respingere" : " · puoi disattivare questi avvisi dal tuo Profilo"),
      pagina: d.comeDecisore ? "approvazione-cartellini" : "",
      rifCollezione: "giustificazioniPresenze", rifId: rif.id, letta: false, createdAt: serverTimestamp(),
    }).catch(() => {})));
  } catch (e) { console.warn("Avvisi della giustificazione non inviati:", e); }
  return rif.id;
}

/** Accetta o respinge (chi decide). Avvisa l'interessato. */
export async function decidiGiustificazione({ giustificazione, esito, nota = "", decisore }) {
  if (!["accettata", "respinta"].includes(esito)) throw new Error("Esito non valido");
  if (giustificazione.uid === decisore.uid) throw new Error("Non puoi decidere una tua giustificazione: serve un'altra persona.");
  const notaPulita = String(nota || "").trim().slice(0, MOTIVO_MAX);
  if (esito === "respinta" && notaPulita.length < 3) throw new Error("Scrivi perché la respingi: la persona lo leggerà.");
  await updateDoc(doc(db, "giustificazioniPresenze", giustificazione.id), {
    stato: esito, decisoDa: decisore.uid, decisoDaNome: decisore.nome || "", decisoIl: serverTimestamp(), notaDecisione: notaPulita,
  });
  const quando = dataLunga(parseData(giustificazione.data));
  await addDoc(collection(db, "notifiche"), {
    userId: giustificazione.uid, orgId: giustificazione.orgId, tipo: "giustificazione_presenze_esito",
    titolo: esito === "accettata" ? `✅ Giustificazione accettata: ${quando}` : `❌ Giustificazione respinta: ${quando}`,
    testo: esito === "accettata"
      ? `${decisore.nome || "Chi approva i cartellini"} l'ha accettata.${notaPulita ? ` «${notaPulita}»` : ""}`
      : `${decisore.nome || "Chi approva i cartellini"}: «${notaPulita}». Inserisci gli orari o scrivi una nuova giustificazione.`,
    pagina: "presenze", rifCollezione: "giustificazioniPresenze", rifId: giustificazione.id, letta: false, createdAt: serverTimestamp(),
  }).catch(() => {});
}

/**
 * Chi, nel giorno indicato, doveva timbrare e ha la timbratura incompleta senza averla
 * giustificata (Home di chi approva i cartellini). Stesse regole della schermata
 * (utils/obbligoPresenze.js). Titolare e admin non sono mai nell'elenco.
 * @returns {Promise<{ data: string, persone: { uid, nome, mancanza }[] } | null>} null se l'obbligo è spento
 */
export async function caricaIncompleti({ orgId, giorno }) {
  const orgSnap = await getDoc(doc(db, "organizations", orgId));
  const org = orgSnap.exists() ? orgSnap.data() : {};
  const livello = livelloObbligo(org);
  if (livello === "spento") return null;
  const ds = dataStr(giorno);
  const attivazione = parseData(org.obbligoPresenzeDal || "");
  if (attivazione && giorno < attivazione) return { data: ds, persone: [] };
  const [uSnap, dSnap, esSnap, progSnap, gSnap] = await Promise.all([
    getDocs(query(collection(db, "users"), where("organizationId", "==", orgId), where("enabled", "==", true))),
    getDocs(query(collection(db, "dipendenti"), where("organizationId", "==", orgId))).catch(() => null),
    getDoc(doc(db, "presenzeEsclusioni", orgId)).catch(() => null),
    getDoc(doc(db, "programmazioniSettimanali", idProgrammazione(orgId, giorno))).catch(() => null),
    getDocs(query(collection(db, "giustificazioniPresenze"), where("orgId", "==", orgId), where("data", "==", ds))).catch(() => null),
  ]);
  const esclusi = esSnap?.exists() ? (esSnap.data().uids || []) : [];
  const dipendenti = dSnap ? dSnap.docs.map(d => ({ id: d.id, ...d.data() })) : [];
  const celle = progSnap?.exists() ? celleDaProgrammazione(progSnap.data()) : [];
  const giustificate = new Set((gSnap ? gSnap.docs.map(d => d.data()) : []).filter(g => g.stato !== "respinta").map(g => g.uid));
  const persone = [];
  for (const d of uSnap.docs) {
    const u = { uid: d.id, ...d.data() };
    if (u.role === "titolare" || u.role === "admin" || giustificate.has(u.uid)) continue;
    const periodo = componiPeriodo(dipendenti.find(x => x.nexoraUid === u.uid), u, esclusi.includes(u.uid));
    if (!periodo.soggetto) continue;
    if (periodo.inizio && giorno < periodo.inizio) continue;
    if (periodo.fine && giorno > periodo.fine) continue;
    if (!dovevaTimbrare(giorno, u.uid, celle)) continue;
    let giorni = {};
    try { const c = await getDoc(doc(db, "presenze", idCartellino(orgId, u.uid, giorno))); giorni = c.exists() ? (c.data().giorni || {}) : {}; }
    catch { continue; }
    const mancanza = mancanzaGiornata(giorni[String(giorno.getDate())]);
    if (mancanza) persone.push({ uid: u.uid, nome: u.displayName || "—", mancanza });
  }
  return { data: ds, persone: persone.sort((a, b) => a.nome.localeCompare(b.nome, "it")) };
}
